import { inject, Injectable, signal } from "@angular/core";
import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { catchError, combineLatest, concatMap, forkJoin, map, Observable, of } from "rxjs";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { GateFaultService } from "../../../../../services/api/gate-fault/gate-fault.service";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { GateFaultUtilsService } from "../../../../../services/utils/gate-fault-utils/gate-fault-utils.service";
import { BotevoAssetCompact } from "../../../../../models/api/asset/botevoAsset";
import { GateFault } from "../../../../../models/api/fault/gateFault";
import { AttributeResponse } from "../../../../../models/shared/telemetry";
import { isNotNullOrUndefined } from "../../../../../shared/utils/utils";
import { DOCUMENT } from "@angular/common";
import { RealtimeGateFaultResponse } from "../../../../../models/ws/realtimeGateFault";
import { RealtimeAttributeResponse } from "../../../../../models/ws/realtimeAttributes";
import {
  GateFaultSubscriptionHandle,
  GateFaultWebsocketService,
} from "../../../../../services/websocket/gate-fault/gate-fault-websocket.service";
import {
  AttributesSubscriptionHandle,
  AttributesWebsocketService,
} from "../../../../../services/websocket/attributes-websocket/attributes-websocket.service";

const TELEMETRY_KEYS: string[] = [
  "numberOfCustomers",
  "readyGateSystems",
  "numberOfGateSystems",
  "numberOfMajorFaults",
  "numberOfCriticalFaults",
  "openServices",
  "openRepairRequests",
];

@Injectable()
export class TenantHomeService {
  private telemetryService = inject(TelemetryService);
  private gateFaultService = inject(GateFaultService);
  private assetService = inject(AssetService);
  private gateFaultUtils = inject(GateFaultUtilsService);
  private attributesWebsocket = inject(AttributesWebsocketService);
  private gateFaultWebsocket = inject(GateFaultWebsocketService);
  private gateFaultSubscription?: GateFaultSubscriptionHandle;
  private attributesSubscription?: AttributesSubscriptionHandle;

  private document = inject(DOCUMENT);
  private localStorage = this.document.defaultView?.localStorage;

  private readonly _noOfCustomers = signal<number | null>(null);
  readonly noOfCustomers = this._noOfCustomers.asReadonly();

  private readonly _availableGates = signal<string | null>(null);
  readonly availableGates = this._availableGates.asReadonly();

  private readonly _openFaults = signal<number[] | null>(null);
  readonly openFaults = this._openFaults.asReadonly();

  private readonly _openServices = signal<number[] | null>(null);
  readonly openServices = this._openServices.asReadonly();

  private readonly _gateFaultState = signal<GateFault[] | null>(null);
  readonly gateFaultState = this._gateFaultState.asReadonly();

  /**
   * Resets all state signals to null.
   */
  private resetStates() {
    this._noOfCustomers.set(null);
    this._availableGates.set(null);
    this._openFaults.set(null);
    this._openServices.set(null);
    this._gateFaultState.set(null);
  }

  /**
   * Loads initial state by fetching attributes, gate faults, and tenant gate systems.
   * @param tenantId - The ID of the tenant.
   */
  public loadInitialState(tenantId: string) {
    this.resetStates();
    const attributes$ = this.fetchAttributes(tenantId);
    const gateFaults$ = this.fetchGateFaults();
    const tenantGateSystems$ = this.fetchTenantGateSystems();

    forkJoin({
      attributes: attributes$,
      gateFaults: gateFaults$,
      tenantGateSystems: tenantGateSystems$,
    }).subscribe((res) => {
      const attributes = res?.attributes;
      const gateSystems = res?.tenantGateSystems;
      if (attributes) {
        const noOfCustomers = attributes?.numberOfCustomers?.value;
        if (isNotNullOrUndefined(noOfCustomers)) {
          this._noOfCustomers.set(noOfCustomers);
        }
        const readyGateSystems = attributes?.readyGateSystems?.value;
        const numberOfGateSystems = attributes?.numberOfGateSystems?.value;
        if (isNotNullOrUndefined(readyGateSystems) && isNotNullOrUndefined(numberOfGateSystems)) {
          const availableGates = readyGateSystems.toString() + "/" + numberOfGateSystems.toString();
          this._availableGates.set(availableGates);
        }
        const numberOfMajorFaults = attributes?.numberOfMajorFaults?.value;
        const numberOfCriticalFaults = attributes?.numberOfCriticalFaults?.value;
        if (isNotNullOrUndefined(numberOfMajorFaults) && isNotNullOrUndefined(numberOfCriticalFaults)) {
          const openFaults = [numberOfMajorFaults, numberOfCriticalFaults];
          this._openFaults.set(openFaults);
        }
        const openContractServices = attributes?.openServices?.value;
        const openRepairRequests = attributes?.openRepairRequests?.value;
        if (isNotNullOrUndefined(openContractServices) && isNotNullOrUndefined(openRepairRequests)) {
          const openServices = [openContractServices, openRepairRequests];
          this._openServices.set(openServices);
        }
      }

      const gateFaults = res?.gateFaults;
      this._gateFaultState.set(gateFaults);

      this.initRealtimeGateFaults(gateSystems);
      this.initRealtimeAttributes(tenantId);
    });
  }
  /**
   * Destroys the real-time WebSocket connections.
   * This is used when the user navigates away from the TenantHome page.
   */
  public destroy() {
    this.resetStates();
    this.gateFaultSubscription?.close();
    this.attributesSubscription?.close();
  }

  /**
   * Initializes real-time gate faults WebSocket connection.
   * @param gateSystemIds - Array of gate system IDs to listen for.
   */

  private initRealtimeGateFaults(gateSystemIds: string[]) {
    this.gateFaultSubscription?.close();
    const subscription = this.gateFaultWebsocket.register({
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: gateSystemIds,
      lang: this.localStorage?.getItem("lang"),
    });
    this.gateFaultSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData: RealtimeGateFaultResponse) => {
        if (incomingData.data) {
          this.gateFaultUtils.handleRealtimeGateFault(
            incomingData.data,
            this._gateFaultState.update.bind(this._gateFaultState)
          );
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Initializes real-time attributes WebSocket connection.
   * @param tenantId - The ID of the tenant.
   * @param token - Authorization token for WebSocket connection.
   */
  private initRealtimeAttributes(tenantId: string) {
    this.attributesSubscription?.close();
    const subscription = this.attributesWebsocket.register({
      entityType: ASSET_TYPES.TENANT,
      entityId: [tenantId],
      keys: ["numberOfGateSystems", "readyGateSystems", "numberOfMajorFaults", "numberOfCriticalFaults"],
    });
    this.attributesSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData: RealtimeAttributeResponse) => {
        this.handleRealtimeAttributes(incomingData.data);
      },
      error: () => undefined,
    });
  }

  /**
   * Handles incoming real-time attributes and updates state signals.
   * @param incomingAttribute - Incoming attribute data.
   */
  private handleRealtimeAttributes(incomingAttribute: AttributeResponse) {
    // Extract and convert all present data points
    const updates: Record<string, number> = {};

    for (const key in incomingAttribute) {
      const entry = incomingAttribute[key];
      if (entry && entry.value !== undefined) {
        updates[key] = entry.value as number;
      }
    }

    // If no valid data points found, return early
    if (Object.keys(updates).length === 0) {
      return;
    }

    Object.entries(updates).forEach(([key, value]) => {
      switch (key) {
        case "numberOfCriticalFaults":
          this._openFaults.update((openFaults) => {
            const newOpenFaults = openFaults ? [...openFaults] : null;
            if (newOpenFaults) {
              newOpenFaults[1] = Number(value);
            }
            return newOpenFaults;
          });
          break;
        case "numberOfMajorFaults":
          this._openFaults.update((openFaults) => {
            const newOpenFaults = openFaults ? [...openFaults] : null;
            if (newOpenFaults) {
              newOpenFaults[0] = Number(value);
            }
            return newOpenFaults;
          });

          break;
        case "readyGateSystems":
          this._availableGates.update((availableGateSystems) => {
            const totalGateSystem = availableGateSystems?.split("/")[1];
            const newAvailableGateSystem = value + "/" + totalGateSystem;
            return newAvailableGateSystem;
          });
          break;
        case "numberOfGateSystems":
          this._availableGates.update((availableGateSystems) => {
            const readyGateSystems = availableGateSystems?.split("/")[0];
            const newAvailableGateSystem = readyGateSystems + "/" + value;
            return newAvailableGateSystem;
          });
          break;
        default:
          break;
      }
    });
  }

  /**
   * Fetches tenant gate systems.
   * @returns An observable with gate systems data.
   */
  private fetchTenantGateSystems() {
    return this.assetService
      .getAssets(ASSET_TYPES.CUSTOMER)
      .pipe(
        concatMap((customers) => {
          const customerGateSystem$: Observable<BotevoAssetCompact[]>[] = [];
          customers.forEach((customer) => {
            if (customer) {
              customerGateSystem$.push(
                this.assetService.getAssets(ASSET_TYPES.GATE_SYSTEM, customer.id, ASSET_TYPES.CUSTOMER).pipe(
                  catchError(() => {
                    return of([]);
                  })
                )
              );
            }
          });
          if (customerGateSystem$.length === 0) {
            return of([]);
          } else {
            return combineLatest(customerGateSystem$);
          }
        })
      )
      .pipe(
        map((res: BotevoAssetCompact[][]) => {
          return res.flat(1).reduce((acc: string[], gateSystem) => {
            if (gateSystem.id) {
              acc.push(gateSystem.id);
            }
            return acc;
          }, []);
        }),
        catchError(() => of([]))
      );
  }

  /**
   * Fetches gate faults.
   * @returns An observable with gate faults data.
   */
  private fetchGateFaults() {
    return this.gateFaultService.getGateFaults(undefined, undefined, true);
  }

  /**
   * Fetches attributes for a tenant.
   * @param tenantId - The ID of the tenant.
   * @returns An observable with attributes data.
   */
  private fetchAttributes(tenantId: string) {
    return this.telemetryService.getAttributes(tenantId, ASSET_TYPES.TENANT, TELEMETRY_KEYS.join(","));
  }
}
